<script setup lang="ts">
import {computed, ref} from "vue";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import CriteriaFieldWidget from "@/views/CriteriaFieldWidget.vue";
import CriteriaOperatorWidget from "@/views/CriteriaOperatorWidget.vue";
import ExpressionEditUtility from "@/utils/common/ExpressionEditUtility.ts";
import CriteriaWidgetPresenter from "@/utils/common/CriteriaWidgetPresenter.ts";
import Translator from "@/utils/common/Translator.ts";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import DeleteIcon from "@/assets/editViewIcons/DeleteIcon.vue";
import DuplicateIcon from "@/assets/editViewIcons/DuplicateIcon.vue";
import {NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import Operator from "@/utils/constants/Operator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DateRangeFieldWidget from "@/components/EditView/widgets/DateRangeFieldWidget.vue";
import QuestionListDropdown from "@/components/questionFields/widgets/QuestionListDropdown.vue";
import CriteriaField = namaTypes.CriteriaField;
import QuestionField = namaTypes.QuestionField;

const model = defineModel<CustomDTOExpression>();
const emit = defineEmits(["removeExpression", "appendNewExpression", "appendCopyExpression", "enterDown"]);
const props = defineProps<{
  criteriaFields: CriteriaField[]
  ownerEntity: string
}>();
const operators = ref(ExpressionEditUtility.getAvailableOperatorsAsOptions(model.value?.field, props.criteriaFields));

function afterUpdateCriteriaField() {
  let availableOperators = ExpressionEditUtility.getAvailableOperatorsAsOptions(model.value?.field, props.criteriaFields);
  operators.value = availableOperators;
  if (!model.value!.operator || !availableOperators.find(l => l.value === model.value!.operator))
    model.value!.operator = availableOperators[0].value;
}

function afterUpdateCriteriaOperator() {
}

const valueWidgetKey = computed(() => {
  if (!model.value!.field || !model.value!.operator)
    return undefined;
  const SEPARATOR = "__";
  const node = model.value!;
  return SEPARATOR + node.field + SEPARATOR + node.id + SEPARATOR;
})

const criteriaField = computed(() => {
  return props.criteriaFields.find(l => l.id === model.value!.field!)!;
})
const createCriteriaPresenter: CriteriaWidgetPresenter = new CriteriaWidgetPresenter(model, props.criteriaFields, props.ownerEntity);

const isOneOfQuestionField = computed<QuestionField>(() => ({
  id: criteriaField.value?.id ?? '',
  metaData: criteriaField.value?.metaData,
  required: false,
} as QuestionField));

const fieldWidget = ref<NamaVueWidget>();
const valueWidget = ref<NamaVueWidget>();

function focusFieldId() {
  if (fieldWidget.value?.focus)
    fieldWidget.value.focus();
}

function focusValueWidget() {
  if (valueWidget.value?.focus)
    valueWidget.value.focus();
  else if (fieldWidget.value?.focus)
    fieldWidget.value.focus();
}

function calcListMode(): "list" | "text" {
  let fieldType = criteriaField.value.metaData?.fieldType;
  if (fieldType === FieldType.Reference)
    return 'list';
  return 'text';
}

defineExpose({focusFieldId, focusValueWidget})
</script>

<template>
  <div
    class="tw-px-1 tw-bg-transparent tw-flex mobile:tw-flex-col tw-w-full tw-gap-2 ultra:tw-gap-1 mobile:tw-gap-0 tw-justify-between tw-items-center"
    @click.stop
       @keydown.f7.prevent="()=>$emit('appendNewExpression')" @keydown.f8.prevent="()=>$emit('appendCopyExpression')"
       @keydown.delete.ctrl.prevent="()=>$emit('removeExpression')">
    <div class="tw-flex tw-flex-1 tw-gap-2 ultra:tw-gap-1 mobile:tw-flex-col  mobile:tw-w-full mobile:tw-gap-[5px] tw-justify-start">
      <div class="tw-flex-none tw-w-[35%] mobile:tw-w-full">
        <CriteriaFieldWidget v-model="model" :criteria-fields="criteriaFields" :owner-entity="ownerEntity"
                             @afterUpdateCriteriaField="afterUpdateCriteriaField" ref="fieldWidget"/>
      </div>
      <div class="tw-flex-none tw-w-[15%] mobile:tw-w-full">
        <CriteriaOperatorWidget v-model="model" :operators="operators" @afterUpdateCriteriaOperator="afterUpdateCriteriaOperator"/>
      </div>
      <div class="tw-flex-1 tw-w-[45%] mobile:tw-w-full">
        <div v-if="model!.field && model?.operator && criteriaField" :key="valueWidgetKey">
          <DateRangeFieldWidget v-if="model?.operator === Operator.WithinPeriod || model?.operator === Operator.OutsidePeriod"
                                :field="criteriaField" :isInsideComposite="true"
                                :fieldMetaData="{...(criteriaField.metaData??{}), fieldId: criteriaField.id, fieldType: FieldType.Text}"
                                :forceEnableField="true" :presenter="createCriteriaPresenter" :doNotApplyThemeLayouts="true"
                                @navigateToNext="()=>$emit('enterDown')" ref="valueWidget"/>
          <QuestionListDropdown v-else-if="model?.operator === Operator.In || model?.operator === Operator.NotIn"
                                :isInsideComposite="true" :presenter="createCriteriaPresenter" :questionField="isOneOfQuestionField"
                                :mode="calcListMode()"
                                :doNotApplyThemeLayouts="true" @navigateToNext="()=>$emit('enterDown')" ref="valueWidget"/>
          <component v-else
                     :is="ExpressionEditUtility.getWidget(criteriaField, createCriteriaPresenter)" :field="criteriaField"
                     :isInsideComposite="true" :fieldMetaData="criteriaField.metaData" :forceEnableField="true"
                     :presenter="createCriteriaPresenter" :doNotApplyThemeLayouts="true"
            @navigateToNext="()=>$emit('enterDown')" ref="valueWidget"/>
        </div>
      </div>
    </div>
    <div class="tw-w-fit tw-shrink-0">
      <div class="tw-flex tw-justify-between tw-items-stretch tw-h-full mobile:tw-gap-2" @click.stop>
        <a
          class="hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
          @click="emit('appendNewExpression')" :title="Translator.translateId('add')+' [F7]'">
          <AddRecordIcon class="tw-size-6 compact:tw-size-5 ultra:tw-size-4 !tw-fill-[#606060] dark:!tw-fill-white"/>
        </a>
        <a
          class="hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
          @click="emit('appendCopyExpression')" :title="Translator.translateId('copy') +' [F8]'">
          <DuplicateIcon class="tw-h-6 compact:tw-h-5 ultra:tw-h-4 !tw-fill-[#606060] dark:!tw-fill-white"/>
        </a>
        <a
          class="hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
          @click="emit('removeExpression')" :title="Translator.translateId('delete')+' [Ctrl+Delete]'">
          <DeleteIcon class="tw-size-6 compact:tw-size-5 ultra:tw-size-4 !tw-fill-[#606060] dark:!tw-fill-white"/>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
